import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Crown, Users, Building, Target, Star, ArrowRight } from "lucide-react";
import { CAREER_STAGES_DATA, CAREER_STAGE_OVERVIEW, type CareerStageInfo } from "@/lib/calculator-education";

interface CareerStageModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedStage?: string;
  onStageSelect?: (stage: string) => void;
}

export function CareerStageModal({ isOpen, onClose, selectedStage, onStageSelect }: CareerStageModalProps) {
  const [activeStage, setActiveStage] = useState(selectedStage || 'Emerging');

  const stageOrder = ['Emerging', 'Established 1', 'Established 2', 'Established 3', 'Mainstream 1', 'Mainstream 2', 'Mainstream 3', 'Mainstream 4'];
  const activeStageInfo = CAREER_STAGES_DATA[activeStage];

  const getStageIcon = (stage: string) => {
    if (stage.includes('Mainstream')) return Crown;
    if (stage.includes('Established')) return Star;
    return Users;
  };

  const getStageColor = (stage: string) => {
    if (stage.includes('Mainstream')) return 'text-purple-600 bg-purple-50 border-purple-200';
    if (stage.includes('Established')) return 'text-blue-600 bg-blue-50 border-blue-200';
    return 'text-gray-600 bg-gray-50 border-gray-200';
  };

  const handleStageSelect = (stage: string) => {
    setActiveStage(stage);
    if (onStageSelect) {
      onStageSelect(stage);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">Artist Career Stages</DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 overflow-hidden">
          <ScrollArea className="h-full pr-4">
            <div className="space-y-6">
              {/* Overview Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-max-primary" />
                    How MAX Thinks About Career Stages
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground leading-relaxed">
                    {CAREER_STAGE_OVERVIEW}
                  </p>
                </CardContent>
              </Card>

              {/* Stage Selection Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {stageOrder.map((stage) => {
                  const Icon = getStageIcon(stage);
                  const isActive = stage === activeStage;
                  
                  return (
                    <Button
                      key={stage}
                      variant={isActive ? "default" : "outline"}
                      onClick={() => handleStageSelect(stage)}
                      className={`h-auto p-3 flex flex-col items-center gap-2 ${
                        isActive ? 'bg-max-primary hover:bg-max-primary/90' : ''
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="text-xs font-medium text-center leading-tight">
                        {stage}
                      </span>
                    </Button>
                  );
                })}
              </div>

              {/* Active Stage Details */}
              {activeStageInfo && (
                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <Badge className={getStageColor(activeStage)}>
                        {activeStageInfo.name}
                      </Badge>
                      <CardTitle>{activeStageInfo.name} Artists</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <p className="text-muted-foreground leading-relaxed">
                      {activeStageInfo.description}
                    </p>

                    <div className="grid md:grid-cols-2 gap-6">
                      {/* Artist Types */}
                      <div>
                        <h4 className="font-semibold mb-3 flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          Artist Types
                        </h4>
                        <ul className="space-y-2">
                          {activeStageInfo.artistTypes.map((type, index) => (
                            <li key={index} className="text-sm text-muted-foreground flex items-start gap-2">
                              <ArrowRight className="w-3 h-3 mt-0.5 flex-shrink-0" />
                              {type}
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Venue Types */}
                      <div>
                        <h4 className="font-semibold mb-3 flex items-center gap-2">
                          <Building className="w-4 h-4" />
                          Venue Types
                        </h4>
                        <ul className="space-y-2">
                          {activeStageInfo.venueTypes.map((type, index) => (
                            <li key={index} className="text-sm text-muted-foreground flex items-start gap-2">
                              <ArrowRight className="w-3 h-3 mt-0.5 flex-shrink-0" />
                              {type}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    {/* Campaign Types */}
                    <div>
                      <h4 className="font-semibold mb-3 flex items-center gap-2">
                        <Target className="w-4 h-4" />
                        Campaign Types
                      </h4>
                      <ul className="grid md:grid-cols-2 gap-2">
                        {activeStageInfo.campaignTypes.map((type, index) => (
                          <li key={index} className="text-sm text-muted-foreground flex items-start gap-2">
                            <ArrowRight className="w-3 h-3 mt-0.5 flex-shrink-0" />
                            {type}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Ideal For */}
                    <div className="bg-max-primary/5 p-4 rounded-lg border border-max-primary/20">
                      <h4 className="font-semibold mb-2 text-max-primary">Ideal For</h4>
                      <p className="text-sm">{activeStageInfo.idealFor}</p>
                    </div>

                    {/* Examples */}
                    <div>
                      <h4 className="font-semibold mb-3">Examples</h4>
                      <div className="flex flex-wrap gap-2">
                        {activeStageInfo.examples.map((example, index) => (
                          <Badge key={index} variant="outline" className="text-xs">
                            {example}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
}